import axios from 'axios';
import { config } from '../config/environment';

class RefreshTokenManager {
  private static instance: RefreshTokenManager;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;
  private failedQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (error: any) => void;
  }> = [];

  private constructor() {}

  static getInstance(): RefreshTokenManager {
    if (!RefreshTokenManager.instance) {
      RefreshTokenManager.instance = new RefreshTokenManager();
    }
    return RefreshTokenManager.instance;
  }

  async refreshToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // If not refreshing, start the refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const newToken = await this.refreshPromise;
      this.processQueue(null, newToken);
      return newToken;
    } catch (error) {
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  async getTokenForRequest(): Promise<string | null> {
    // If currently refreshing, wait for it to complete
    if (this.isRefreshing && this.refreshPromise) {
      return new Promise((resolve, reject) => {
        this.addToQueue(resolve, reject);
      });
    }

    // Check if we have a valid main app token
    const token = localStorage.getItem("token");
    if (token) {
      return token;
    }

    // No token available, try to refresh
    return this.refreshToken();
  }

  private async performRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refreshToken");
    
    if (!refreshToken) {
      console.warn('RefreshTokenManager: No refresh token available');
      throw new Error('No refresh token available');
    }

    console.log('RefreshTokenManager: Starting token refresh...');
    
    try {
      // Use a separate axios instance to avoid interceptor loops
      const response = await axios.post(`${config.API_URL}/auth/refresh`, {
        refreshToken,
      }, {
        timeout: config.REQUEST_TIMEOUT,
        withCredentials: config.USE_COOKIES,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.data?.success && response.data?.data) {
        const { token, refreshToken: newRefreshToken } = response.data.data;
        
        // Update localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("refreshToken", newRefreshToken);
        
        console.log('RefreshTokenManager: Token refresh successful');
        return token;
      } else {
        console.error('RefreshTokenManager: Invalid refresh response:', response.data);
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('RefreshTokenManager: Token refresh failed:', error);
      
      // Refresh failed, clear auth data
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      
      // Redirect to login
      window.location.href = "/login";
      
      throw error;
    }
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  addToQueue(resolve: (token: string | null) => void, reject: (error: any) => void) {
    this.failedQueue.push({ resolve, reject });
  }

  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

export const refreshTokenManager = RefreshTokenManager.getInstance();
export default refreshTokenManager;
